import type { InfiniteData } from "@tanstack/react-query";

import type { MeetingAssetState, ZoomMeetingSummaryView } from "../types";
import {
  mediaMayStillArrive,
  meetingClockRef,
} from "../zoom_meeting_summaries/RecordingPlayer";

export const ASSET_KINDS = [
  "screen",
  "camera",
  "audio",
  "transcript",
  "summary",
] as const;

export type AssetKind = (typeof ASSET_KINDS)[number];

/** The kinds a player track can be, as opposed to transcript and summary. */
export type TrackKind = Extract<AssetKind, "screen" | "camera" | "audio">;

export type AssetDisplayState =
  | Exclude<MeetingAssetState, null>
  | "waiting"
  | "none";

type Row = Pick<
  ZoomMeetingSummaryView,
  | "screen_state"
  | "camera_state"
  | "audio_state"
  | "transcript_state"
  | "summary_count"
  | "meeting_end_time"
  | "meeting_start_time"
>;

const FILE_STATE: Record<Exclude<AssetKind, "summary">, keyof Row> = {
  screen: "screen_state",
  camera: "camera_state",
  audio: "audio_state",
  transcript: "transcript_state",
};

const windowOpen = (r: Row, now: number) =>
  mediaMayStillArrive(meetingClockRef(r), now);

/**
 * What one asset of a meeting looks like on the list. The view decides
 * available / processing / failed from the files it has; the frontend adds
 * "waiting" for an asset with no file yet while the meeting is young enough for
 * Zoom to still deliver one, the same 90-minute window the meeting page uses.
 */
export const assetState = (
  r: Row,
  kind: AssetKind,
  now: number = Date.now(),
): AssetDisplayState => {
  if (kind === "summary") {
    if ((r.summary_count ?? 0) > 0) return "available";
    return windowOpen(r, now) ? "waiting" : "none";
  }
  const state = r[FILE_STATE[kind]] as MeetingAssetState | undefined;
  if (state) return state;
  return windowOpen(r, now) ? "waiting" : "none";
};

/**
 * No file of any kind has arrived and the meeting ended recently: the row reads
 * "Waiting for Zoom". Summaries do not count. A saved summary says nothing about
 * whether Zoom's recording is still on its way.
 */
export const isAwaitingZoom = (r: Row, now: number = Date.now()) =>
  windowOpen(r, now) &&
  !r.screen_state &&
  !r.camera_state &&
  !r.audio_state &&
  !r.transcript_state;

export const AWAITING_REFETCH_MS = 30 * 1000;

/**
 * Poll while any asset on a loaded row is still moving: waiting for Zoom, or
 * uploading. Stopping at the first file would freeze a row on "uploading" until
 * a reload, which is the moment people are watching it.
 *
 * Both states end on their own, so polling always stops: waiting closes with
 * the 90-minute window (the meeting's age, never files being absent), and the
 * view stops calling an upload processing 24 hours after its row was created.
 */
export const refetchWhileAwaiting = (
  rows: Row[] | undefined,
  now: number = Date.now(),
): number | false =>
  rows?.some((r) =>
    ASSET_KINDS.some((kind) => {
      const state = assetState(r, kind, now);
      return state === "waiting" || state === "processing";
    }),
  )
    ? AWAITING_REFETCH_MS
    : false;

/**
 * Rows from whatever a list query holds: `useGetList` data is a plain array (or
 * `{ data }` before ra-core unwraps it), `InfiniteList` data is pages.
 */
export const rowsFromQueryData = <T>(raw: unknown): T[] => {
  if (Array.isArray(raw)) return raw as T[];
  const infinite = raw as InfiniteData<{ data: T[] }> | undefined;
  if (infinite?.pages) return infinite.pages.flatMap((p) => p.data ?? []);
  const single = raw as { data?: T[] } | undefined;
  return single?.data ?? [];
};
